import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Avatar,
  IconButton,
  Button,
  Tooltip,
  Stack,
  Divider,
  Badge,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  BugReport as BugIcon,
  Task as TaskIcon,
  Assignment as StoryIcon,
  SubdirectoryArrowRight as SubtaskIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Flag as FlagIcon
} from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Drag and Drop Item Types
const ItemTypes = {
  TICKET: 'ticket'
};

// Ticket Card Component
const TicketCard = ({ ticket, onEdit, onDelete }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.TICKET,
    item: { id: ticket.id, status: ticket.status },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  }));

  const getTypeIcon = (type) => {
    switch (type) {
      case 'bug': return <BugIcon fontSize="small" color="error" />;
      case 'task': return <TaskIcon fontSize="small" color="primary" />;
      case 'story': return <StoryIcon fontSize="small" color="info" />;
      case 'subtask': return <SubtaskIcon fontSize="small" color="secondary" />;
      default: return <TaskIcon fontSize="small" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getAssigneeInitials = (assignee) => {
    if (!assignee) return '?';
    return assignee.charAt(0).toUpperCase();
  };

  return (
    <Card
      ref={drag}
      sx={{
        mb: 1,
        cursor: 'move',
        opacity: isDragging ? 0.5 : 1,
        '&:hover': {
          boxShadow: 3,
        },
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack spacing={1}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" alignItems="center" spacing={1}>
              {getTypeIcon(ticket.type)}
              <Typography variant="body2" fontWeight="bold" color="text.secondary">
                #{ticket.id}
              </Typography>
            </Stack>
            <IconButton size="small" onClick={() => onEdit(ticket)}>
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Stack>

          {/* Title */}
          <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.3 }}>
            {ticket.title || ticket.summary}
          </Typography>

          {/* Labels */}
          {ticket.labels && ticket.labels.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {ticket.labels.slice(0, 3).map((label, index) => (
                <Chip
                  key={index}
                  label={label}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              ))}
              {ticket.labels.length > 3 && (
                <Chip
                  label={`+${ticket.labels.length - 3}`}
                  size="small"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Stack>
          )}

          {/* Footer */}
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              {/* Priority */}
              {ticket.priority && (
                <Chip
                  icon={<FlagIcon />}
                  label={ticket.priority}
                  size="small"
                  color={getPriorityColor(ticket.priority)}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              
              {/* Time estimate */}
              {ticket.estimatedTime && (
                <Chip
                  icon={<TimeIcon />}
                  label={ticket.estimatedTime}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Stack>

            {/* Assignee */}
            {ticket.assignee && (
              <Tooltip title={ticket.assignee}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    fontSize: '0.7rem',
                    bgcolor: 'primary.main',
                  }}
                >
                  {getAssigneeInitials(ticket.assignee)}
                </Avatar>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

// Column Component
const Column = ({ title, status, tickets, onEdit, onDelete, onMoveTicket, onCreateTicket }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.TICKET,
    drop: (item) => {
      if (item.status !== status) {
        onMoveTicket(item.id, status);
      }
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper
      ref={drop}
      sx={{
        minHeight: 400,
        p: 2,
        bgcolor: isOver ? 'action.hover' : 'background.paper',
        border: isOver ? `2px dashed ${theme.palette.primary.main}` : '1px solid',
        borderColor: isOver ? 'primary.main' : 'divider',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <Stack spacing={2}>
        {/* Column Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>
            <Badge badgeContent={tickets.length} color="primary" />
          </Stack>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onCreateTicket(status)}
            sx={{ minWidth: 'auto', px: 1 }}
          >
            {!isMobile && 'Add'}
          </Button>
        </Stack>

        <Divider />

        {/* Tickets */}
        <Box sx={{ minHeight: 300 }}>
          {tickets.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 200,
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No tickets
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Drag tickets here or click Add
              </Typography>
            </Box>
          ) : (
            tickets.map((ticket) => (
              <TicketCard
                key={ticket.id}
                ticket={ticket}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

// Main Board Component
const Board = ({ tickets, onEditTicket, onDeleteTicket, onCreateTicket, onMoveTicket }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Group tickets by status
  const ticketsByStatus = useMemo(() => {
    const groups = {
      todo: [],
      inprogress: [],
      done: [],
      blocked: []
    };

    tickets.forEach(ticket => {
      const status = ticket.status?.toLowerCase() || 'todo';
      if (groups[status]) {
        groups[status].push(ticket);
      } else {
        groups.todo.push(ticket);
      }
    });

    return groups;
  }, [tickets]);

  const columns = [
    { title: 'To Do', status: 'todo', tickets: ticketsByStatus.todo },
    { title: 'In Progress', status: 'inprogress', tickets: ticketsByStatus.inprogress },
    { title: 'Done', status: 'done', tickets: ticketsByStatus.done },
    { title: 'Blocked', status: 'blocked', tickets: ticketsByStatus.blocked },
  ];

  return (
    <Box sx={{ p: 2, height: '100vh', overflow: 'hidden' }}>
      <DndProvider backend={HTML5Backend}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
            gap: 2,
            height: '100%',
            overflow: 'auto',
          }}
        >
          {columns.map((column) => (
            <Column
              key={column.status}
              title={column.title}
              status={column.status}
              tickets={column.tickets}
              onEdit={onEditTicket}
              onDelete={onDeleteTicket}
              onCreateTicket={onCreateTicket}
              onMoveTicket={onMoveTicket}
            />
          ))}
        </Box>
      </DndProvider>
    </Box>
  );
};

export default Board;